import { Box, Typography } from '@mui/material'
import { green, common, orange } from '@mui/material/colors'
import { isSameDay, differenceInDays, isBefore, startOfDay } from 'date-fns'
import PropTypes from 'prop-types'
import React, { useMemo, useRef } from 'react'

import { formatDateToBEFormatDateFns, createDateFromString } from 'utils/date'

import { RENTAL_FEE_PRICING_PERIOD } from '../../common/settings'

import { isDateInAnyRentalPeriod } from './rentalPeriodUtils'
import { EVENT_BADGE_COLOR, StyledRentalFeeDay } from './styles'

const { ONCE } = RENTAL_FEE_PRICING_PERIOD

const RentalFeeCalendarDay = ({
  day,
  pickersDayProps,
  startDate,
  endDate,
  pendingSelectionDate,
  gracePeriodDays = 0,
  rentalPeriods = [],
  rentalPeriodDates,
  rentalPeriodStartDates,
  rentalPeriodEndDates,
  billingStatusByDate = {},
  eventsByDate,
  dayPrice,
  dayPriceByDate,
  pricingPeriod,
  isOngoingRentalFee = false,
  periodContext = null,
  onDayClick,
}) => {
  const isWithinClosedPeriod = periodContext === 'closed'
  const dayObj = day.day || day
  const isValidDate = dayObj instanceof Date && !Number.isNaN(dayObj.getTime())

  const dateStr = useMemo(() => (isValidDate ? formatDateToBEFormatDateFns(dayObj) : null), [isValidDate, dayObj])

  const isRentalPeriodStartDate = useMemo(() => {
    if (!dateStr || !rentalPeriodStartDates) return false
    return rentalPeriodStartDates.has(dateStr)
  }, [dateStr, rentalPeriodStartDates])

  const isRentalPeriodEndDate = useMemo(() => {
    if (!dateStr || !rentalPeriodEndDates) return false
    return rentalPeriodEndDates.has(dateStr)
  }, [dateStr, rentalPeriodEndDates])

  const isStartDate = useMemo(
    () => isValidDate && ((startDate && isSameDay(dayObj, startDate)) || isRentalPeriodStartDate),
    [isValidDate, startDate, dayObj, isRentalPeriodStartDate]
  )

  const isEndDate = useMemo(
    () => isValidDate && ((endDate && isSameDay(dayObj, endDate)) || isRentalPeriodEndDate),
    [isValidDate, endDate, dayObj, isRentalPeriodEndDate]
  )

  const isStartEndSame = useMemo(() => isStartDate && isEndDate, [isStartDate, isEndDate])

  const isInRange = useMemo(() => {
    if (!isValidDate) return false

    const isInSelectionRange = startDate && endDate && dayObj > startDate && dayObj < endDate

    const isInExistingPeriod =
      dateStr &&
      isDateInAnyRentalPeriod(dateStr, rentalPeriods).found &&
      !rentalPeriodStartDates?.has(dateStr) &&
      !rentalPeriodEndDates?.has(dateStr)

    return isInSelectionRange || isInExistingPeriod
  }, [isValidDate, startDate, endDate, dayObj, dateStr, rentalPeriods, rentalPeriodStartDates, rentalPeriodEndDates])

  const isToday = useMemo(() => {
    if (!isValidDate) return false
    const today = new Date()
    return isSameDay(dayObj, today)
  }, [isValidDate, dayObj])

  const isDisabled = useMemo(() => {
    if (!isValidDate || !isOngoingRentalFee || !startDate) return false
    return isBefore(dayObj, startDate) && !isSameDay(dayObj, startDate)
  }, [isValidDate, isOngoingRentalFee, startDate, dayObj])

  const isPendingSelection = useMemo(
    () => isValidDate && pendingSelectionDate && isSameDay(dayObj, pendingSelectionDate) && !isStartDate && !isEndDate,
    [isValidDate, pendingSelectionDate, dayObj, isStartDate, isEndDate]
  )

  const isNewPeriodSelection = useMemo(() => {
    if (!startDate || !rentalPeriods.length) return true
    const startDateStr = formatDateToBEFormatDateFns(startDate)
    return !rentalPeriods.some(period => period.startDate === startDateStr)
  }, [startDate, rentalPeriods])

  const dayRentalPeriodContext = useMemo(() => {
    const today = startOfDay(new Date())
    const currentRentalPeriod = isNewPeriodSelection
      ? [{ startDate: formatDateToBEFormatDateFns(startDate), endDate: formatDateToBEFormatDateFns(endDate ?? today) }]
      : []
    const rentalPeriodResult = isDateInAnyRentalPeriod(dateStr, [...rentalPeriods, ...currentRentalPeriod])
    const periodStartDate =
      rentalPeriodResult.found && rentalPeriodResult.startDate ? createDateFromString(rentalPeriodResult.startDate) : startDate
    const dayPosition = differenceInDays(dayObj, periodStartDate)
    const isInGracePeriod = dayPosition > 0 && dayPosition < gracePeriodDays
    return { isInRentalPeriod: rentalPeriodResult.found, isInGracePeriod }
  }, [dateStr, rentalPeriods, isNewPeriodSelection, startDate, endDate, dayObj, gracePeriodDays])

  const selectionBillingStatus = useMemo(() => {
    if (isStartDate) return 'START_DATE'
    if (isEndDate) return 'END_DATE'

    if (dateStr && billingStatusByDate[dateStr]) {
      return billingStatusByDate[dateStr]
    }

    if (dayRentalPeriodContext.isInRentalPeriod) {
      if (dayRentalPeriodContext.isInGracePeriod) return 'GRACE_PERIOD'
      return 'UNBILLED'
    }

    return null
  }, [
    isStartDate,
    isEndDate,
    dateStr,
    billingStatusByDate,
    dayRentalPeriodContext.isInRentalPeriod,
    dayRentalPeriodContext.isInGracePeriod,
  ])

  const eventCount = useMemo(() => {
    if (!dateStr || !eventsByDate) return 0
    return eventsByDate[dateStr] || 0
  }, [dateStr, eventsByDate])

  const isInRentalPeriod = useMemo(() => {
    if (!dateStr || !rentalPeriodDates) return false
    return rentalPeriodDates.has(dateStr)
  }, [dateStr, rentalPeriodDates])

  const displayPrice = useMemo(() => {
    const periodType = pricingPeriod?.toUpperCase()
    if (periodType === ONCE) return dayPrice
    return dateStr && dayPriceByDate ? dayPriceByDate[dateStr] : null
  }, [pricingPeriod, dayPrice, dateStr, dayPriceByDate])

  const showPriceBadge = useMemo(() => {
    const periodType = pricingPeriod?.toUpperCase()

    if (periodType === ONCE) {
      return isEndDate && dayPrice != null
    }

    if (dateStr && billingStatusByDate[dateStr] === 'GRACE_PERIOD') {
      return null
    }

    if (dayRentalPeriodContext.isInGracePeriod) {
      return null
    }

    if (!dayRentalPeriodContext.isInRentalPeriod && !isStartDate && !isEndDate) {
      return null
    }

    return displayPrice != null
  }, [
    pricingPeriod,
    isStartDate,
    isEndDate,
    dayPrice,
    displayPrice,
    dateStr,
    billingStatusByDate,
    dayRentalPeriodContext.isInGracePeriod,
    dayRentalPeriodContext.isInRentalPeriod,
  ])

  const dayRef = useRef(null)

  const handleClick = event => {
    if (!onDayClick || !isValidDate || isDisabled) return

    const anchor = event?.currentTarget ?? dayRef.current
    onDayClick(dayObj, anchor, periodContext)
  }

  if (!isValidDate) {
    return null
  }

  return (
    <Box
      ref={dayRef}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: '100%',
        height: '100%',
      }}
    >
      <StyledRentalFeeDay
        {...pickersDayProps}
        billingStatus={selectionBillingStatus}
        day={dayObj}
        disabled={isDisabled}
        isEndDate={isEndDate}
        isInRange={isInRange}
        isInRentalPeriod={isInRentalPeriod}
        isPendingSelection={isPendingSelection}
        isRentalPeriodEndDate={isRentalPeriodEndDate}
        isRentalPeriodStartDate={isRentalPeriodStartDate}
        isStartDate={isStartDate}
        isStartEndSame={isStartEndSame}
        isToday={isToday}
        isWithinClosedPeriod={isWithinClosedPeriod}
        onDaySelect={handleClick}
      />
      {showPriceBadge && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            minWidth: 20,
            height: 14,
            borderRadius: '8px',
            backgroundColor: selectionBillingStatus === 'BILLED' ? green[500] : orange[500],
            borderColor: common.white,
            borderWidth: 1,
            borderStyle: 'solid',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.5,
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: 8,
              fontWeight: 500,
              color: common.white,
              lineHeight: 1,
            }}
          >
            {`$${displayPrice}`}
          </Typography>
        </Box>
      )}
      {eventCount > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 2,
            right: showPriceBadge ? 'auto' : 2,
            left: showPriceBadge ? 2 : 'auto',
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: EVENT_BADGE_COLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: 8,
              fontWeight: 700,
              color: 'white',
              lineHeight: 1,
            }}
          >
            {eventCount}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

RentalFeeCalendarDay.propTypes = {
  day: PropTypes.object.isRequired,
  pickersDayProps: PropTypes.object.isRequired,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  pendingSelectionDate: PropTypes.instanceOf(Date),
  gracePeriodDays: PropTypes.number,
  rentalPeriods: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    })
  ),
  rentalPeriodDates: PropTypes.instanceOf(Set),
  rentalPeriodStartDates: PropTypes.instanceOf(Set),
  rentalPeriodEndDates: PropTypes.instanceOf(Set),
  billingStatusByDate: PropTypes.object,
  eventsByDate: PropTypes.object,
  dayPrice: PropTypes.number,
  dayPriceByDate: PropTypes.object,
  pricingPeriod: PropTypes.string,
  isOngoingRentalFee: PropTypes.bool,
  periodContext: PropTypes.oneOf(['closed', 'ongoing', 'outside', null]),
  onDayClick: PropTypes.func,
}

export default RentalFeeCalendarDay
